import React, { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { estimateReadingTime } from '../../services/blogService';

const BlogList = ({ posts }) => {
  const { category, tag } = useParams();
  const [query, setQuery] = useState('');

  // Get unique categories and tags
  const categories = useMemo(() =>
    [...new Set(posts.map(post => post.category || 'General'))],
    [posts]
  );

  // Filter posts
  const filteredPosts = useMemo(() => {
    let result = posts;
    if (category) {
      result = result.filter(post => (post.category || '').toLowerCase() === category.toLowerCase());
    } else if (tag) {
      result = result.filter(post => (post.tags || []).some(t => (t || '').toLowerCase() === tag.toLowerCase()));
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      result = result.filter(p => (
        (p.title || '').toLowerCase().includes(q) ||
        (p.excerpt || '').toLowerCase().includes(q) ||
        ((p.content_md || p.content || '')).toLowerCase().includes(q)
      ));
    }
    return result;
  }, [posts, category, tag, query]);

  const featuredPost = !category && !tag && !query && filteredPosts.length > 0 ? filteredPosts[0] : null;
  const gridPosts = featuredPost ? filteredPosts.slice(1) : filteredPosts;

  return (
    <>
      <Helmet>
        <title>Blog | Ravana Migration</title>
        <meta name="description" content="Expert insights about studying in Germany and international migration." />
      </Helmet>

      <div className="min-h-screen bg-gray-50">

        {/* Corporate Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-16 sm:py-20 lg:py-24">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="relative inline-block">
                  <span className="relative z-10">Migration Insights</span>
                  <svg className="absolute -bottom-2 left-0 w-full h-6 text-blue-400 opacity-30" viewBox="0 0 200 20" fill="currentColor"><path d="M0 15 Q 50 5 100 15 T 200 15 L 200 20 L 0 20 Z" /></svg>
                </span>
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Expert advice, success stories, and the latest updates on your journey to Germany.
              </p>
            </div>

            {featuredPost && (
              <div className="mt-16 bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative h-64 md:h-auto overflow-hidden">
                    <img
                      src={featuredPost.image_url || featuredPost.imageUrl}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8 md:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-bold uppercase tracking-wide">
                        {featuredPost.category}
                      </span>
                      <span className="text-gray-400 text-sm">•</span>
                      <span className="text-gray-500 text-sm font-medium">
                        {new Date(featuredPost.date_published || featuredPost.date).toLocaleDateString()}
                      </span>
                    </div>
                    <Link to={`/blog/${featuredPost.id}`} className="block group">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                        {featuredPost.title}
                      </h2>
                    </Link>
                    <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                          {featuredPost.author?.[0]}
                        </div>
                        <div className="text-sm">
                          <p className="font-bold text-gray-900">{featuredPost.author}</p>
                          <p className="text-gray-500">{estimateReadingTime(featuredPost.content_md || featuredPost.content)}</p>
                        </div>
                      </div>
                      <Link to={`/blog/${featuredPost.id}`} className="text-blue-600 font-semibold hover:text-blue-800 flex items-center gap-1 transition-colors">
                        Read more <span aria-hidden="true">&rarr;</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Controls */}
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 mb-12">
            <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
              <Link
                to="/blog"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${!category ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
              >
                All Posts
              </Link>
              {categories.map(cat => (
                <Link
                  key={cat}
                  to={`/blog/category/${cat.toLowerCase()}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${category === cat.toLowerCase() ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                >
                  {cat}
                </Link>
              ))}
            </div>

            <div className="relative w-full lg:w-80">
              <input
                type="text"
                placeholder="Search articles..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all shadow-sm"
              />
              <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </div>
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {gridPosts.map(post => (
              <article key={post.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col overflow-hidden group hover:-translate-y-1">
                <div className="relative h-48 overflow-hidden">
                  <img src={post.image_url || post.imageUrl} alt={post.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-2 py-1 rounded text-xs font-bold text-gray-800 shadow-sm">
                    {estimateReadingTime(post.content_md || post.content)}
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{post.category}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <time className="text-xs text-gray-500">{new Date(post.date_published || post.date).toLocaleDateString()}</time>
                  </div>

                  <Link to={`/blog/${post.id}`} className="block mb-3">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                  </Link>

                  <p className="text-gray-600 text-sm line-clamp-3 mb-6 flex-1">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                    <span className="text-sm font-medium text-gray-700">{post.author}</span>
                    <div className="flex items-center gap-1 text-gray-400 group-hover:text-pink-500 transition-colors">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" /></svg>
                      <span className="text-xs font-bold">{post.likes || 0}</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm mt-8">
              <div className="text-gray-300 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your search or filters.</p>
              <button onClick={() => { setQuery(''); }} className="text-blue-600 font-medium hover:text-blue-800">Clear all filters</button>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default BlogList;
